import OpenSidebarIcon from '@assets/images/common/icon/channel/open_sidebar.svg';
import SettingUserIcon from '@assets/images/common/icon/setting/setting-user.svg';
import SvgIconSetting from '@assets/images/common/icon/setting/SvgIconSetting';
import { Channel } from '@src/api/shared/community/channelApiClient';
import { brandColors } from '@src/assets/colors/brandColors';
import { grayColor } from '@src/assets/colors/defaultColors';
import SearchIcon from '@src/assets/images/common/icon/search/SearchIcon';
import useTooltip from '@src/hooks/useTooltip';
import { SHARED_URL } from '@src/routes/shared';
import { Emoji } from 'emoji-picker-react';
import React, { RefObject } from 'react';
import { generatePath, Link } from 'react-router-dom';

import { TOOLTIP_DISPLAY_TIME } from './chat/chatList/ChatItemHeader';
import Tooltip from './chat/Tooltip';
import DisplayNameUpdateBanner from './DisplayNameUpdateBanner';

interface CommunityChannelHeaderProps {
  channel: Channel | null;
  openSettingMenu: boolean;
  menuRef: RefObject<HTMLDivElement>;
  isTenantAdmin: boolean;
  isLoggedIn: boolean;
  code: string | undefined;
  toggleMobileSidebar: () => void;
  handleSettingMenuClick: () => void;
  handleChannelSettingOpenButtonClick: () => void;
  handleLinkClick: (e: React.MouseEvent<HTMLElement>, to: string) => void;
  navigate: (path: string) => void;
  showDisplayNameBanner?: boolean;
  isSticky?: boolean;
  showBorder?: boolean;
  containerClassName?: string;
  innerContainerClassName?: string;
}

function CommunityChannelHeader({
  channel,
  openSettingMenu,
  menuRef,
  isTenantAdmin,
  isLoggedIn,
  code,
  toggleMobileSidebar,
  handleSettingMenuClick,
  handleChannelSettingOpenButtonClick,
  handleLinkClick,
  navigate,
  showDisplayNameBanner = true,
  isSticky = true,
  containerClassName = '',
  innerContainerClassName = '',
}: CommunityChannelHeaderProps) {
  const { showTooltip, displayTooltip } = useTooltip();

  const handleSearchClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      displayTooltip?.(TOOLTIP_DISPLAY_TIME);
      return;
    }

    handleLinkClick(e, generatePath(SHARED_URL.COMMUNITY_CHANNEL_CHAT_SEARCH));
  };
  const containerClasses = `flex items-center justify-between bg-white ${
    isSticky ? 'sticky top-0 z-40' : ''
  } ${containerClassName}`.trim();

  const innerContainerClasses =
    `flex w-full items-center border-b border-solid border-b-gray-200 pl-6 lg:pl-2 h-[42px] sm:h-[60px] ${innerContainerClassName}`.trim();
  return (
    <>
      {showDisplayNameBanner && <DisplayNameUpdateBanner />}
      <div className={containerClasses}>
        <div className={innerContainerClasses}>
          <button
            className='hidden size-[32px] place-items-center lg:grid'
            onClick={toggleMobileSidebar}
            data-setting-menu=''
          >
            <img
              src={OpenSidebarIcon}
              alt=''
              width={16}
              height={16}
              className='size-[16px]'
            />
          </button>
          <div className='flex w-full items-center lg:w-[calc(100%-32px)]'>
            <div className='flex w-11/12 items-center gap-2 lg:flex-col lg:items-start lg:gap-0 sm:w-9/12'>
              <h1
                className={`flex items-center gap-2 truncate text-[18px] font-bold lg:text-[16px]
                  ${channel?.description ? 'max-w-[60%] lg:max-w-full' : 'w-full'}
                `}
                title={channel?.name}
              >
                <Emoji
                  unified={channel?.emoji || ''}
                  size={20}
                  key={`emoji-header-${channel?.emoji}`}
                />
                <span className='truncate'>{channel?.name}</span>
              </h1>
              {channel?.description && (
                <>
                  <div className='h-[18px] w-px bg-gray-200 lg:hidden' />
                  <p
                    className='truncate text-[16px] text-gray-400 lg:max-w-full lg:text-[12px]'
                    title={channel.description}
                  >
                    {channel.description}
                  </p>
                </>
              )}
            </div>

            <div className='flex w-1/12 items-center justify-end pr-2 sm:w-3/12'>
              <div className='relative'>
                <Link
                  to={generatePath(SHARED_URL.COMMUNITY_CHANNEL_CHAT_SEARCH)}
                  className='relative grid size-[42px] cursor-pointer place-items-center'
                  onClick={(e) => {
                    handleSearchClick(e);
                  }}
                >
                  <SearchIcon
                    color='#888888'
                    className='size-6'
                  />
                </Link>
                {showTooltip && (
                  <div className='absolute right-0 z-50 w-max whitespace-nowrap text-[12px]'>
                    <Tooltip
                      label='表示するには、ログインしてください。'
                      weight='500'
                    />
                  </div>
                )}
              </div>
              <div
                className='relative grid place-items-center'
                ref={menuRef}
              >
                {isTenantAdmin ? (
                  <>
                    <button
                      className='grid place-items-center'
                      onClick={handleSettingMenuClick}
                      data-setting-menu=''
                    >
                      <SvgIconSetting
                        color={openSettingMenu ? brandColors['500'] : grayColor['450']}
                        style={{ width: '24px', height: '24px' }}
                      />
                    </button>
                    {openSettingMenu && (
                      <div
                        className='absolute right-[26px] top-[50px] z-50 w-[192px] overflow-hidden rounded-md border border-gray-200 bg-white text-sm'
                        data-account-menu=''
                      >
                        <div className='flex flex-col gap-px bg-gray-200'>
                          <button
                            type='button'
                            className='cursor-pointer bg-white px-6 py-[15px] text-left transition-colors hover:bg-primary-500 hover:text-white'
                            onClick={handleChannelSettingOpenButtonClick}
                          >
                            チャンネル設定
                          </button>
                          <Link
                            to={generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code })}
                            className='cursor-pointer bg-white px-6 py-[15px] text-left transition-colors hover:bg-primary-500 hover:text-white'
                            onClick={(e) => {
                              handleLinkClick(
                                e,
                                generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code }),
                              );
                            }}
                          >
                            チャンネル参加者一覧
                          </Link>
                        </div>
                      </div>
                    )}
                  </>
                ) : isLoggedIn ? (
                  <button
                    className='grid size-[42px] place-items-center'
                    onClick={() =>
                      navigate(generatePath(SHARED_URL.COMMUNITY_CHANNEL_MEMBERS, { code }))
                    }
                    data-setting-menu=''
                  >
                    <img
                      src={SettingUserIcon}
                      alt=''
                      width={24}
                      height={24}
                      className='size-[24px]'
                    />
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default CommunityChannelHeader;
